import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Shield, FileText, CheckCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGREEMENTS = {
  teacher: {
    titleAr: 'اتفاقية المعلم المستقل',
    titleEn: 'Independent Teacher Agreement',
    key: 'teacher_agreement'
  },
  student: {
    titleAr: 'اتفاقية الطالب',
    titleEn: 'Student Agreement',
    key: 'student_agreement'
  },
  parent: {
    titleAr: 'اتفاقية ولي الأمر',
    titleEn: 'Parent/Guardian Agreement',
    key: 'parent_agreement'
  }
};

const LegalAgreementGate = ({ children }) => {
  const { user, getAuthHeader } = useAuth();
  const { language, localize } = useLanguage();
  const t = (ar, en) => localize({ ar, en });

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin' && !user.is_child_account) {
      checkAgreementStatus();
    } else {
      setLoading(false);
    }
  }, [user]);

  const checkAgreementStatus = async () => {
    try {
      const res = await axios.get(`${API}/legal/agreement-status`, { headers: getAuthHeader() });
      setStatus(res.data);
    } catch {
      setStatus({ accepted: false, role: user?.role });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!accepted) return;
    setSubmitting(true);
    try {
      const agreementInfo = AGREEMENTS[user.role];
      await axios.post(`${API}/legal/accept-agreement`, {
        agreement_type: agreementInfo?.key || `${user.role}_agreement`,
        ip_address: 'web-client',
        user_agent: navigator.userAgent
      }, { headers: getAuthHeader() });
      setStatus(prev => ({ ...prev, accepted: true }));
    } catch {
      // retry silently
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-emerald-500 text-lg">{t('جاري التحميل...', 'Loading...')}</div>
      </div>
    );
  }

  if (!user || user.role === 'admin' || user.is_child_account || status?.accepted) {
    return children;
  }

  const agreementConfig = AGREEMENTS[user.role];

  return (
    <div className={`min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`} dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="legal-agreement-gate">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 px-8 py-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-7 h-7" />
            <h1 className="text-xl font-bold">{t(agreementConfig?.titleAr, agreementConfig?.titleEn)}</h1>
          </div>
          <p className="text-emerald-100 text-sm">
            {t('يرجى قراءة الاتفاقية بالكامل والموافقة عليها للمتابعة', 'Please read the full agreement and accept to continue')}
          </p>
        </div>

        {/* Agreement Content */}
        <ScrollArea className="h-[55vh] px-8 py-6" onScroll={(e) => {
          const el = e.target;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) setScrolledToBottom(true);
        }}>
          <div onScroll={(e) => {
            const el = e.target;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) setScrolledToBottom(true);
          }}>
            <AgreementContent role={user.role} language={language} />
          </div>
        </ScrollArea>

        {/* Accept Section */}
        <div className="border-t border-slate-200 px-8 py-5 bg-slate-50 space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="accept-agreement"
              checked={accepted}
              onCheckedChange={setAccepted}
              data-testid="accept-agreement-checkbox"
            />
            <label htmlFor="accept-agreement" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
              {user.role === 'teacher'
                ? t('أقر بأنني قرأت وفهمت اتفاقية المعلم المستقل بالكامل، وأوافق على جميع الشروط والأحكام الواردة فيها، وأتحمل المسؤولية الكاملة عن خدماتي التعليمية.',
                    'I confirm that I have read and understood the Independent Teacher Agreement in full, agree to all terms and conditions, and assume full responsibility for my teaching services.')
                : user.role === 'parent'
                ? t('أقر بأنني ولي أمر الطالب المسجل، وأنني قرأت وفهمت هذه الاتفاقية بالكامل، وأوافق على جميع الشروط نيابةً عن ابني/ابنتي.',
                    'I confirm that I am the registered student\'s parent/guardian, have read and understood this agreement in full, and agree to all terms on behalf of my child.')
                : t('أقر بأنني أبلغ من العمر 18 عاماً أو أكثر، وأنني قرأت وفهمت هذه الاتفاقية بالكامل وأوافق على جميع شروطها.',
                    'I confirm that I am 18 years of age or older, have read and understood this agreement in full, and agree to all its terms.')
              }
            </label>
          </div>
          <Button
            onClick={handleAccept}
            disabled={!accepted || submitting}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-base"
            data-testid="accept-agreement-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('جاري المعالجة...', 'Processing...')}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {t('أوافق على الاتفاقية', 'I Accept the Agreement')}
              </span>
            )}
          </Button>
          <p className="text-xs text-slate-400 text-center">
            {t('بالموافقة، أنت تقر بقراءتك وفهمك الكامل لهذه الاتفاقية الملزمة قانونياً',
              'By accepting, you acknowledge that you have fully read and understood this legally binding agreement')}
          </p>
        </div>
      </div>
    </div>
  );
};

const AgreementContent = ({ role, language }) => {
  const t = (ar, en) => localize({ ar, en });

  if (role === 'teacher') return <TeacherAgreement t={t} />;
  if (role === 'parent') return <ParentAgreement t={t} />;
  return <StudentAgreement t={t} />;
};

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-bold text-slate-900 mt-8 mb-3 pb-2 border-b border-slate-100">{children}</h2>
);
const SubTitle = ({ children }) => (
  <h3 className="text-base font-semibold text-slate-800 mt-5 mb-2">{children}</h3>
);
const P = ({ children }) => <p className="text-sm text-slate-600 leading-relaxed mb-3">{children}</p>;
const Li = ({ children }) => <li className="text-sm text-slate-600 leading-relaxed mb-1.5">{children}</li>;
const Important = ({ children }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-4 flex items-start gap-3">
    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
    <p className="text-sm text-amber-800 font-medium leading-relaxed">{children}</p>
  </div>
);

// ===================== TEACHER AGREEMENT =====================
const TeacherAgreement = ({ t }) => (
  <div data-testid="teacher-agreement-content">
    <div className="text-center mb-6">
      <h1 className="text-xl font-black text-slate-900 mb-1">{t('اتفاقية المعلم المستقل', 'Independent Teacher Agreement')}</h1>
      <p className="text-xs text-slate-400">{t('النسخة 1.0 - أبريل 2026', 'Version 1.0 - April 2026')}</p>
      <p className="text-sm text-slate-500 mt-2">{t('منصة Learn With Trust - تابعة لشركة Trust Ed Techno for Development and Innovation L.L.C.', 'Learn With Trust Platform - A subsidiary of Trust Ed Techno for Development and Innovation L.L.C.')}</p>
    </div>

    <Important>{t(
      'هذه الاتفاقية ملزمة قانونياً. بموافقتك عليها، تُقر بأنك فهمت جميع بنودها وتلتزم بتنفيذها.',
      'This agreement is legally binding. By accepting it, you acknowledge that you have understood all its terms and are committed to abiding by them.'
    )}</Important>

    <SectionTitle>{t('المادة الأولى: طبيعة العلاقة التعاقدية', 'Article 1: Nature of Contractual Relationship')}</SectionTitle>
    <P>{t(
      '1.1 يُقر المعلم بأنه مقدم خدمات مستقل (Independent Contractor) وليس موظفاً لدى شركة Trust Ed Techno for Development and Innovation L.L.C. أو منصة Learn With Trust.',
      '1.1 The Teacher acknowledges that they are an Independent Contractor and not an employee of Trust Ed Techno for Development and Innovation L.L.C. or the Learn With Trust platform.'
    )}</P>
    <P>{t(
      '1.2 لا تنشأ بين الطرفين علاقة عمل أو شراكة أو وكالة أو تمثيل من أي نوع. المعلم مسؤول بشكل كامل ومستقل عن خدماته التعليمية.',
      '1.2 No employment, partnership, agency, or representation relationship of any kind is established between the parties. The Teacher is fully and independently responsible for their educational services.'
    )}</P>
    <P>{t(
      '1.3 المنصة تعمل كوسيط تقني فقط لتسهيل الربط بين المعلمين والطلاب، ولا تتدخل في محتوى أو طريقة تقديم الدروس.',
      '1.3 The platform operates solely as a technology intermediary to facilitate the connection between teachers and students, and does not interfere with the content or delivery method of lessons.'
    )}</P>
    <P>{t(
      '1.4 لا يحق للمعلم المطالبة بأي حقوق وظيفية بما في ذلك، على سبيل المثال لا الحصر: الرواتب الثابتة، الإجازات المدفوعة، التأمين الصحي، مكافأة نهاية الخدمة، أو أي مزايا وظيفية أخرى.',
      '1.4 The Teacher has no right to claim any employment benefits including but not limited to: fixed salaries, paid leave, health insurance, end-of-service gratuity, or any other employment benefits.'
    )}</P>

    <SectionTitle>{t('المادة الثانية: التزامات المعلم', 'Article 2: Teacher Obligations')}</SectionTitle>
    <ul className="list-disc pr-6 space-y-1">
      <Li>{t('تقديم خدمات تعليمية عالية الجودة وفقاً لأعلى المعايير المهنية.', 'Provide high-quality educational services according to the highest professional standards.')}</Li>
      <Li>{t('الالتزام بالمواعيد المحددة للدروس والحضور في الوقت المتفق عليه.', 'Adhere to scheduled lesson times and attend at the agreed-upon time.')}</Li>
      <Li>{t('التعامل مع الطلاب بأدب واحترام وحيادية مطلقة بغض النظر عن الجنسية أو الدين أو العرق.', 'Treat students with courtesy, respect, and absolute impartiality regardless of nationality, religion, or ethnicity.')}</Li>
      <Li>{t('عدم استخدام أي لغة أو سلوك غير لائق أو مسيء أثناء الدروس أو المراسلات.', 'Refrain from using any inappropriate or offensive language or behavior during lessons or communications.')}</Li>
      <Li>{t('تقديم مؤهلات وشهادات صحيحة وحقيقية عند التسجيل، والمنصة تحتفظ بحقها في التحقق منها.', 'Provide valid and authentic qualifications and certificates upon registration; the platform reserves the right to verify them.')}</Li>
      <Li>{t('الالتزام الكامل بقوانين حماية الطفل في دولة الإمارات العربية المتحدة (القانون الاتحادي رقم 3 لسنة 2016 - قانون وديمة).', 'Fully comply with child protection laws in the UAE (Federal Law No. 3 of 2016 - Wadeema\'s Law).')}</Li>
      <Li>{t('الإبلاغ الفوري عن أي سلوك مشبوه أو مخالف يتعلق بسلامة الطلاب.', 'Immediately report any suspicious or inappropriate behavior related to student safety.')}</Li>
    </ul>

    <SectionTitle>{t('المادة الثالثة: المحظورات', 'Article 3: Prohibitions')}</SectionTitle>
    <ul className="list-disc pr-6 space-y-1">
      <Li>{t('يُمنع منعاً باتاً التواصل مع الطلاب أو أولياء الأمور خارج المنصة بهدف تجاوز عمولة المنصة.', 'It is strictly prohibited to communicate with students or parents outside the platform to circumvent platform commissions.')}</Li>
      <Li>{t('يُمنع مشاركة معلومات الاتصال الشخصية (رقم الهاتف، البريد الإلكتروني الشخصي، حسابات التواصل الاجتماعي) مع الطلاب.', 'Sharing personal contact information (phone number, personal email, social media accounts) with students is prohibited.')}</Li>
      <Li>{t('يُمنع تقديم أي محتوى يتعارض مع القيم والقوانين المعمول بها في دولة الإمارات.', 'Providing any content that conflicts with the values and laws applicable in the UAE is prohibited.')}</Li>
      <Li>{t('يُمنع التمييز بين الطلاب أو رفض تقديم الخدمة على أساس الجنس أو العرق أو الدين أو الجنسية.', 'Discrimination between students or refusal to provide service based on gender, race, religion, or nationality is prohibited.')}</Li>
      <Li>{t('يُمنع تسجيل الحصص أو نقل محتواها لأي طرف ثالث دون موافقة كتابية مسبقة.', 'Recording lessons or transferring their content to any third party without prior written consent is prohibited.')}</Li>
    </ul>

    <SectionTitle>{t('المادة الرابعة: الرسوم والعمولات', 'Article 4: Fees and Commissions')}</SectionTitle>
    <P>{t(
      '4.1 تستقطع المنصة نسبة 20% من قيمة كل درس مدفوع كرسوم خدمة مقابل تسهيل عملية الربط والحجز والدفع.',
      '4.1 The platform deducts 20% of each paid lesson\'s value as a service fee for facilitating connection, booking, and payment.'
    )}</P>
    <P>{t(
      '4.2 يحق للمعلم طلب سحب أرباحه المتراكمة وفقاً لسياسة السحب المعتمدة من المنصة.',
      '4.2 The Teacher may request withdrawal of accumulated earnings according to the platform\'s approved withdrawal policy.'
    )}</P>
    <P>{t(
      '4.3 تحتفظ المنصة بحق تعديل نسبة العمولة مع إخطار المعلمين مسبقاً بمدة لا تقل عن 30 يوماً.',
      '4.3 The platform reserves the right to modify the commission rate with at least 30 days prior notice to teachers.'
    )}</P>

    <SectionTitle>{t('المادة الخامسة: إخلاء المسؤولية', 'Article 5: Disclaimer of Liability')}</SectionTitle>
    <Important>{t(
      'هذا البند جوهري في هذه الاتفاقية ويُعد شرطاً أساسياً لقبول المعلم في المنصة.',
      'This clause is fundamental to this agreement and is a prerequisite for the Teacher\'s acceptance on the platform.'
    )}</Important>
    <P>{t(
      '5.1 المنصة غير مسؤولة بأي شكل من الأشكال عن جودة أو محتوى أو نتائج الدروس المقدمة من المعلم.',
      '5.1 The platform bears no responsibility whatsoever for the quality, content, or results of lessons provided by the Teacher.'
    )}</P>
    <P>{t(
      '5.2 المنصة غير مسؤولة عن أي ضرر مادي أو معنوي أو جسدي ينتج عن تعامل المعلم مع الطلاب سواء داخل أو خارج المنصة.',
      '5.2 The platform is not liable for any material, moral, or physical harm resulting from the Teacher\'s dealings with students, whether inside or outside the platform.'
    )}</P>
    <P>{t(
      '5.3 المعلم يتحمل المسؤولية القانونية الكاملة عن أفعاله وسلوكه وخدماته، ويتعهد بتعويض المنصة عن أي خسائر أو مطالبات تنشأ نتيجة إخلاله بهذه الاتفاقية.',
      '5.3 The Teacher assumes full legal responsibility for their actions, behavior, and services, and undertakes to indemnify the platform against any losses or claims arising from breach of this agreement.'
    )}</P>
    <P>{t(
      '5.4 المنصة غير مسؤولة عن انقطاع الخدمة لأسباب تقنية أو خارجة عن السيطرة (القوة القاهرة).',
      '5.4 The platform is not responsible for service interruptions due to technical reasons or force majeure.'
    )}</P>

    <SectionTitle>{t('المادة السادسة: إنهاء الاتفاقية', 'Article 6: Termination')}</SectionTitle>
    <P>{t(
      '6.1 يحق للمنصة تعليق أو إنهاء حساب المعلم فوراً ودون إنذار مسبق في حالة مخالفة أي من بنود هذه الاتفاقية.',
      '6.1 The platform has the right to suspend or terminate the Teacher\'s account immediately and without prior notice in case of violation of any terms of this agreement.'
    )}</P>
    <P>{t(
      '6.2 يحق للمعلم إنهاء هذه الاتفاقية في أي وقت بإلغاء حسابه، مع الالتزام بإتمام أي دروس محجوزة مسبقاً.',
      '6.2 The Teacher may terminate this agreement at any time by canceling their account, while committing to complete any previously booked lessons.'
    )}</P>

    <SectionTitle>{t('المادة السابعة: القانون الحاكم', 'Article 7: Governing Law')}</SectionTitle>
    <P>{t(
      '7.1 تخضع هذه الاتفاقية لقوانين دولة الإمارات العربية المتحدة، وتختص محاكم إمارة أبوظبي بالنظر في أي نزاع ينشأ عنها.',
      '7.1 This agreement is governed by the laws of the United Arab Emirates, and the courts of the Emirate of Abu Dhabi shall have jurisdiction over any disputes arising therefrom.'
    )}</P>
    <P>{t(
      '7.2 في حالة وجود تعارض بين النسختين العربية والإنجليزية من هذه الاتفاقية، تُعتمد النسخة العربية.',
      '7.2 In case of any conflict between the Arabic and English versions of this agreement, the Arabic version shall prevail.'
    )}</P>

    <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
      <p className="text-xs text-slate-500">{t('© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م. جميع الحقوق محفوظة.', '© 2026 Trust Ed Techno for Development and Innovation L.L.C. All rights reserved.')}</p>
    </div>
  </div>
);

// ===================== STUDENT AGREEMENT =====================
const StudentAgreement = ({ t }) => (
  <div data-testid="student-agreement-content">
    <div className="text-center mb-6">
      <h1 className="text-xl font-black text-slate-900 mb-1">{t('اتفاقية استخدام المنصة - الطالب', 'Platform Usage Agreement - Student')}</h1>
      <p className="text-xs text-slate-400">{t('النسخة 1.0 - أبريل 2026', 'Version 1.0 - April 2026')}</p>
      <p className="text-sm text-slate-500 mt-2">{t('منصة Learn With Trust - تابعة لشركة Trust Ed Techno for Development and Innovation L.L.C.', 'Learn With Trust Platform - A subsidiary of Trust Ed Techno for Development and Innovation L.L.C.')}</p>
    </div>

    <Important>{t(
      'يجب أن يكون عمر الطالب 18 عاماً أو أكثر لاستخدام المنصة بشكل مستقل. الطلاب دون 18 عاماً يجب تسجيلهم عبر حساب ولي الأمر.',
      'Students must be 18 years or older to use the platform independently. Students under 18 must be registered through a parent/guardian account.'
    )}</Important>

    <SectionTitle>{t('المادة الأولى: طبيعة المنصة', 'Article 1: Nature of the Platform')}</SectionTitle>
    <P>{t(
      '1.1 منصة Learn With Trust هي منصة تقنية تربط بين الطلاب والمعلمين المستقلين. المنصة ليست مؤسسة تعليمية ولا تقدم خدمات تعليمية بشكل مباشر.',
      '1.1 Learn With Trust is a technology platform connecting students with independent teachers. The platform is not an educational institution and does not provide educational services directly.'
    )}</P>
    <P>{t(
      '1.2 المعلمون المسجلون في المنصة هم مقدمو خدمات مستقلون وليسوا موظفين لدى المنصة.',
      '1.2 Teachers registered on the platform are independent service providers and are not employees of the platform.'
    )}</P>
    <P>{t(
      '1.3 المنصة غير مسؤولة عن تقييم كفاءة المعلمين أو ضمان نتائج تعليمية محددة.',
      '1.3 The platform is not responsible for evaluating teachers\' competence or guaranteeing specific educational outcomes.'
    )}</P>

    <SectionTitle>{t('المادة الثانية: شروط التسجيل', 'Article 2: Registration Requirements')}</SectionTitle>
    <ul className="list-disc pr-6 space-y-1">
      <Li>{t('يجب أن يكون المستخدم بعمر 18 عاماً أو أكثر للتسجيل كطالب مستقل.', 'Users must be 18 years or older to register as an independent student.')}</Li>
      <Li>{t('تقديم بيانات شخصية صحيحة ودقيقة عند التسجيل.', 'Provide accurate and truthful personal information upon registration.')}</Li>
      <Li>{t('الحفاظ على سرية بيانات تسجيل الدخول وعدم مشاركتها مع أي طرف آخر.', 'Maintain the confidentiality of login credentials and not share them with any third party.')}</Li>
      <Li>{t('الطالب مسؤول عن جميع الأنشطة التي تتم من خلال حسابه.', 'The student is responsible for all activities conducted through their account.')}</Li>
    </ul>

    <SectionTitle>{t('المادة الثالثة: التزامات الطالب', 'Article 3: Student Obligations')}</SectionTitle>
    <ul className="list-disc pr-6 space-y-1">
      <Li>{t('احترام المعلم والتعامل معه بأدب ولياقة.', 'Respect the teacher and treat them with courtesy.')}</Li>
      <Li>{t('الالتزام بمواعيد الدروس المحجوزة والحضور في الوقت المحدد.', 'Adhere to booked lesson times and attend punctually.')}</Li>
      <Li>{t('عدم تسجيل الحصص دون إذن مسبق من المعلم.', 'Do not record lessons without prior permission from the teacher.')}</Li>
      <Li>{t('عدم استخدام المنصة لأي أغراض غير قانونية أو غير أخلاقية.', 'Do not use the platform for any illegal or unethical purposes.')}</Li>
      <Li>{t('الإبلاغ الفوري عن أي سلوك غير لائق من أي معلم.', 'Immediately report any inappropriate behavior from any teacher.')}</Li>
    </ul>

    <SectionTitle>{t('المادة الرابعة: الدفع والاسترداد', 'Article 4: Payment and Refunds')}</SectionTitle>
    <P>{t(
      '4.1 تُدفع رسوم الدروس مقدماً عبر بوابة الدفع الإلكتروني المعتمدة في المنصة.',
      '4.1 Lesson fees are paid in advance through the platform\'s approved electronic payment gateway.'
    )}</P>
    <P>{t(
      '4.2 يخضع استرداد الرسوم لسياسة الإلغاء المعتمدة والمنشورة في المنصة.',
      '4.2 Refunds are subject to the cancellation policy approved and published on the platform.'
    )}</P>
    <P>{t(
      '4.3 خلال الفترة المجانية، جميع الحجوزات مجانية ولا تترتب عليها أي رسوم.',
      '4.3 During the free period, all bookings are free and no fees apply.'
    )}</P>

    <SectionTitle>{t('المادة الخامسة: إخلاء المسؤولية', 'Article 5: Disclaimer of Liability')}</SectionTitle>
    <Important>{t(
      'هذا البند جوهري ويُعد شرطاً أساسياً لاستخدام المنصة.',
      'This clause is fundamental and is a prerequisite for using the platform.'
    )}</Important>
    <P>{t(
      '5.1 المنصة غير مسؤولة عن جودة أو محتوى أو نتائج الدروس المقدمة من المعلمين المستقلين.',
      '5.1 The platform is not responsible for the quality, content, or results of lessons provided by independent teachers.'
    )}</P>
    <P>{t(
      '5.2 المنصة غير مسؤولة عن أي ضرر مادي أو معنوي أو جسدي قد ينتج عن استخدام المنصة أو التعامل مع المعلمين.',
      '5.2 The platform is not liable for any material, moral, or physical harm that may result from using the platform or dealing with teachers.'
    )}</P>
    <P>{t(
      '5.3 المنصة لا تضمن توفر الخدمة بشكل مستمر ودون انقطاع، وغير مسؤولة عن أي خسائر ناتجة عن انقطاع الخدمة.',
      '5.3 The platform does not guarantee continuous and uninterrupted service availability and is not responsible for any losses resulting from service interruptions.'
    )}</P>
    <P>{t(
      '5.4 الطالب يستخدم المنصة على مسؤوليته الشخصية الكاملة.',
      '5.4 The student uses the platform at their own full personal responsibility.'
    )}</P>

    <SectionTitle>{t('المادة السادسة: القانون الحاكم', 'Article 6: Governing Law')}</SectionTitle>
    <P>{t(
      'تخضع هذه الاتفاقية لقوانين دولة الإمارات العربية المتحدة، وتختص محاكم إمارة أبوظبي بالنظر في أي نزاع ينشأ عنها.',
      'This agreement is governed by the laws of the United Arab Emirates, and the courts of the Emirate of Abu Dhabi shall have jurisdiction over any disputes arising therefrom.'
    )}</P>

    <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
      <p className="text-xs text-slate-500">{t('© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م. جميع الحقوق محفوظة.', '© 2026 Trust Ed Techno for Development and Innovation L.L.C. All rights reserved.')}</p>
    </div>
  </div>
);

// ===================== PARENT AGREEMENT =====================
const ParentAgreement = ({ t }) => (
  <div data-testid="parent-agreement-content">
    <div className="text-center mb-6">
      <h1 className="text-xl font-black text-slate-900 mb-1">{t('اتفاقية ولي الأمر / الوصي القانوني', 'Parent/Legal Guardian Agreement')}</h1>
      <p className="text-xs text-slate-400">{t('النسخة 1.0 - أبريل 2026', 'Version 1.0 - April 2026')}</p>
      <p className="text-sm text-slate-500 mt-2">{t('منصة Learn With Trust - تابعة لشركة Trust Ed Techno for Development and Innovation L.L.C.', 'Learn With Trust Platform - A subsidiary of Trust Ed Techno for Development and Innovation L.L.C.')}</p>
    </div>

    <Important>{t(
      'بصفتك ولي أمر / وصي قانوني، أنت المسؤول الأول عن سلامة طفلك أثناء استخدام المنصة.',
      'As a parent/legal guardian, you are primarily responsible for your child\'s safety while using the platform.'
    )}</Important>

    <SectionTitle>{t('المادة الأولى: طبيعة المنصة', 'Article 1: Nature of the Platform')}</SectionTitle>
    <P>{t(
      '1.1 منصة Learn With Trust هي منصة تقنية تربط بين الطلاب والمعلمين المستقلين في دولة الإمارات العربية المتحدة. المنصة ليست مؤسسة تعليمية ولا تقدم خدمات تعليمية بشكل مباشر.',
      '1.1 Learn With Trust is a technology platform connecting students with independent teachers in the UAE. The platform is not an educational institution and does not provide educational services directly.'
    )}</P>
    <P>{t(
      '1.2 المعلمون هم مقدمو خدمات مستقلون. المنصة تسهل عملية الربط فقط ولا تتحمل مسؤولية اختيار أو مراقبة المعلمين.',
      '1.2 Teachers are independent service providers. The platform only facilitates connections and does not bear responsibility for selecting or monitoring teachers.'
    )}</P>

    <SectionTitle>{t('المادة الثانية: الموافقة والإقرار', 'Article 2: Consent and Acknowledgment')}</SectionTitle>
    <P>{t(
      '2.1 بموافقتك على هذه الاتفاقية، تُقر بأنك ولي الأمر الشرعي أو الوصي القانوني للطالب/الطلاب المسجلين تحت حسابك.',
      '2.1 By accepting this agreement, you confirm that you are the legal parent or guardian of the student(s) registered under your account.'
    )}</P>
    <P>{t(
      '2.2 تُقر بموافقتك على تسجيل طفلك في المنصة واستخدامه لخدماتها تحت إشرافك ومسؤوليتك الكاملة.',
      '2.2 You confirm your consent to register your child on the platform and for them to use its services under your full supervision and responsibility.'
    )}</P>
    <P>{t(
      '2.3 تُقر بعلمك بأن المنصة توفر ميزة المراقبة الصامتة (Stealth Monitoring) التي تمكنك من متابعة الحصة الافتراضية دون علم الطرفين، وأن استخدام هذه الميزة اختياري ومسؤوليتك.',
      '2.3 You acknowledge that the platform provides a Stealth Monitoring feature that allows you to observe virtual lessons without either party\'s knowledge, and that using this feature is optional and your responsibility.'
    )}</P>

    <SectionTitle>{t('المادة الثالثة: مسؤوليات ولي الأمر', 'Article 3: Parent Responsibilities')}</SectionTitle>
    <ul className="list-disc pr-6 space-y-1">
      <Li>{t('الإشراف على استخدام الطفل للمنصة والتأكد من سلامته أثناء الدروس.', 'Supervise the child\'s use of the platform and ensure their safety during lessons.')}</Li>
      <Li>{t('تقديم بيانات صحيحة ودقيقة عن الطفل بما في ذلك العمر والمرحلة الدراسية.', 'Provide accurate information about the child including age and grade level.')}</Li>
      <Li>{t('الإبلاغ الفوري عن أي سلوك غير لائق من أي معلم تجاه الطفل.', 'Immediately report any inappropriate behavior from any teacher toward the child.')}</Li>
      <Li>{t('مراجعة تقارير الدروس والمتابعة الدورية لتقدم الطفل.', 'Review lesson reports and periodically follow up on the child\'s progress.')}</Li>
      <Li>{t('التأكد من أن الطفل يتعامل بأدب واحترام مع المعلمين.', 'Ensure the child treats teachers with courtesy and respect.')}</Li>
      <Li>{t('عدم السماح لأي شخص غير الطفل المسجل باستخدام حسابه.', 'Do not allow anyone other than the registered child to use their account.')}</Li>
    </ul>

    <SectionTitle>{t('المادة الرابعة: حماية الطفل', 'Article 4: Child Protection')}</SectionTitle>
    <P>{t(
      '4.1 تلتزم المنصة بتطبيق قانون حماية الطفل (وديمة) - القانون الاتحادي رقم 3 لسنة 2016 في دولة الإمارات العربية المتحدة.',
      '4.1 The platform is committed to applying the Child Protection Law (Wadeema) - Federal Law No. 3 of 2016 in the UAE.'
    )}</P>
    <P>{t(
      '4.2 توفر المنصة نظام مراقبة صامتة يتيح لولي الأمر متابعة الحصة الافتراضية في أي وقت.',
      '4.2 The platform provides a stealth monitoring system allowing parents to observe virtual lessons at any time.'
    )}</P>
    <P>{t(
      '4.3 يحق لولي الأمر تقييم المعلم والإبلاغ عنه في حالة وجود أي مخالفة.',
      '4.3 Parents have the right to rate teachers and report them in case of any violation.'
    )}</P>

    <SectionTitle>{t('المادة الخامسة: إخلاء المسؤولية', 'Article 5: Disclaimer of Liability')}</SectionTitle>
    <Important>{t(
      'هذا البند جوهري ويُعد شرطاً أساسياً لاستخدام المنصة.',
      'This clause is fundamental and is a prerequisite for using the platform.'
    )}</Important>
    <P>{t(
      '5.1 المنصة وسيط تقني فقط، وغير مسؤولة عن أي أفعال أو تصرفات يقوم بها المعلمون المستقلون.',
      '5.1 The platform is solely a technology intermediary and is not responsible for any actions or conduct of independent teachers.'
    )}</P>
    <P>{t(
      '5.2 المنصة غير مسؤولة عن أي ضرر مادي أو معنوي أو جسدي أو نفسي قد يلحق بالطفل نتيجة استخدام المنصة أو التعامل مع المعلمين.',
      '5.2 The platform is not liable for any material, moral, physical, or psychological harm to the child resulting from using the platform or dealing with teachers.'
    )}</P>
    <P>{t(
      '5.3 ولي الأمر يتحمل المسؤولية الكاملة عن مراقبة استخدام طفله للمنصة وسلامته.',
      '5.3 The parent assumes full responsibility for monitoring their child\'s use of the platform and their safety.'
    )}</P>
    <P>{t(
      '5.4 المنصة لا تضمن كفاءة المعلمين أو نتائج تعليمية محددة. اختيار المعلم مسؤولية ولي الأمر بالكامل.',
      '5.4 The platform does not guarantee teachers\' competence or specific educational outcomes. Teacher selection is entirely the parent\'s responsibility.'
    )}</P>
    <P>{t(
      '5.5 يتعهد ولي الأمر بتعويض المنصة عن أي خسائر أو مطالبات تنشأ عن إساءة استخدام الحساب من قبله أو من قبل طفله.',
      '5.5 The parent undertakes to indemnify the platform against any losses or claims arising from misuse of the account by themselves or their child.'
    )}</P>

    <SectionTitle>{t('المادة السادسة: البيانات الشخصية', 'Article 6: Personal Data')}</SectionTitle>
    <P>{t(
      '6.1 بموافقتك على هذه الاتفاقية، تُقر بموافقتك على جمع ومعالجة البيانات الشخصية لطفلك وفقاً لسياسة الخصوصية المنشورة في المنصة.',
      '6.1 By accepting this agreement, you consent to the collection and processing of your child\'s personal data in accordance with the privacy policy published on the platform.'
    )}</P>
    <P>{t(
      '6.2 تلتزم المنصة بحماية بيانات الأطفال وفقاً لقانون حماية البيانات الشخصية (القانون الاتحادي رقم 45 لسنة 2021).',
      '6.2 The platform is committed to protecting children\'s data in accordance with the Personal Data Protection Law (Federal Law No. 45 of 2021).'
    )}</P>

    <SectionTitle>{t('المادة السابعة: القانون الحاكم', 'Article 7: Governing Law')}</SectionTitle>
    <P>{t(
      'تخضع هذه الاتفاقية لقوانين دولة الإمارات العربية المتحدة، وتختص محاكم إمارة أبوظبي بالنظر في أي نزاع ينشأ عنها. في حالة وجود تعارض بين النسختين العربية والإنجليزية، تُعتمد النسخة العربية.',
      'This agreement is governed by the laws of the UAE, and the courts of Abu Dhabi shall have jurisdiction over any disputes. In case of conflict between Arabic and English versions, the Arabic version shall prevail.'
    )}</P>

    <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
      <p className="text-xs text-slate-500">{t('© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م. جميع الحقوق محفوظة.', '© 2026 Trust Ed Techno for Development and Innovation L.L.C. All rights reserved.')}</p>
    </div>
  </div>
);

export default LegalAgreementGate;
